import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Col, Row } from 'react-bootstrap'
import * as fromActions from 'store/actions/blocks.action'
import * as fromEvaluationActions from 'store/actions/evaluation.action'
import { IMainState } from 'store/states'
import Loader from 'components/ui/loader/Loader'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import Button, {
  ButtonSizes,
  ButtonVariant,
} from 'design-system/button/src/Button'
import {
  IOldEvaluationQuestion,
  IOldEvaluationQuestionOption,
} from 'models/oldEvaluation.model'
import { EvaluationResult } from 'models/evaluationResult.model'
import { PathRoutes } from 'enum/routes'
import { IBlockInstanceModel } from 'models/block.model'
import EvaluationModal from './components/evaluation-modal/EvaluationModal'
import EvaluationAlert from './components/evaluation-alert/EvaluationAlert'
import EvaluationQuestion from './components/evaluation-question/EvaluationQuestion'
import styles from './Evaluation.module.scss'

interface IEvaluation {
  courseId?: number | string
  evaluationId: number | string
  canEdit?: boolean
  setOnEvaluation?: any
  blockInstances?: IBlockInstanceModel[]
  lessonId?: number
  onClose?: () => void
  state: EvaluationStatesEnum
  onSuccess?: () => void
}
export interface IUserAnswer {
  likertId: number
  id: number
  questionId: number
}

const Evaluation: React.FC<IEvaluation> = ({
  courseId,
  evaluationId,
  canEdit = false,
  setOnEvaluation = undefined,
  lessonId,
  blockInstances = [],
  onClose,
  state,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [evaluationData, setEvaluationData] = useState<EvaluationResult>()
  const [answers, setAnswers] = useState<IUserAnswer[]>([])
  const history = useHistory()

  const [showResultsModal, setShowResultsModal] = useState(false)
  const dispatch = useDispatch()
  const [auxLoading, setLoading] = useState(false)
  const { evaluation: data, loading } = useSelector(
    ({ blocks }: IMainState) => blocks
  )
  const questionsLength = data.questions?.length
  const canFinish = useMemo(() => {
    return (
      state === EvaluationStatesEnum.inProcess &&
      answers.length > 0 &&
      answers.filter((e) => e).length === questionsLength
    )
  }, [answers, questionsLength, state])
  const sendEvaluation = useRef(false)

  useEffect(() => {
    const { onPreview, inProcess } = EvaluationStatesEnum
    dispatch({
      type: fromActions.GET_EVALUATION_DATA,
      payload: {
        id: state !== inProcess ? Number(evaluationId) : evaluationId,
        preview: state === onPreview,
      },
    })
  }, [evaluationId, dispatch, state])

  useEffect(() => {
    const { questions = [] } = data
    const { onCheck, onPreview } = EvaluationStatesEnum
    if (loading) return

    if (
      state === EvaluationStatesEnum.onCheck &&
      (!data.config?.showResults || data.results.attemps === 0)
    ) {
      history.push(PathRoutes.MAIN)
    }

    const aux: IUserAnswer[] = []
    switch (state) {
      case onCheck:
        questions.map(
          ({
            answer: { questionsLikertLabelId: id },
            questionableId,
          }: IOldEvaluationQuestion) => {
            aux.push({ id, likertId: questionableId, questionId: 0 })
          }
        )
        setAnswers(aux)
        break
      case onPreview:
        questions.map(
          ({ content, questionableId: questionId }: IOldEvaluationQuestion) =>
            content.map(
              ({ id, likertId, checked }: IOldEvaluationQuestionOption) => {
                checked && aux.push({ id, likertId, questionId })
              }
            )
        )
        setAnswers(aux)
        break
      default:
        break
    }
    if (sendEvaluation.current) {
      setShowResultsModal(true)
      sendEvaluation.current = false
    }
  }, [data, history, loading, state])

  const handleSetAnswers = (index: number, data: IUserAnswer) => {
    const aux = answers.map((answer) => ({ ...answer }))
    aux[index] = data
    setAnswers(aux)
  }

  const handleSend = () => {
    if (!courseId || !lessonId || !data.id) return
    sendEvaluation.current = true

    dispatch(
      fromEvaluationActions.Actions.sendEvaluation({
        answers,
        courseId,
        blockEvaluationId: data.id,
        blockInstanceId: data.blockInstanceableId,
        lessonId,
        blockInstances,
        onSuccess: (data) => {
          setShowResultsModal(true)
          setEvaluationData(data)
          setLoading(false)
          dispatch({
            type: fromActions.GET_EVALUATION_DATA,
            payload: {
              id:
                state !== EvaluationStatesEnum.inProcess
                  ? Number(evaluationId)
                  : evaluationId,
              preview: state === EvaluationStatesEnum.onPreview,
            },
          })
          onSuccess?.()
        },
        onError: () => {
          setLoading(false)
        },
      })
    )

    setLoading(true)
  }

  const handleOnClose = () => {
    setShowResultsModal(false)
    setOnEvaluation && setOnEvaluation()
  }

  return auxLoading ? (
    <Loader color />
  ) : (
    <div
      className={
        state != EvaluationStatesEnum.inProcess ? styles.container : ''
      }
    >
      <Row className="m-0">
        {state !== EvaluationStatesEnum.inProcess && (
          <Col xs={2}>
            <div className={styles.sidebar}>
              <div
                className={`${styles.onlyMobile} ${styles.sidebarResponsiveHeader}`}
              >
                <div className="col-sm">
                  <span className={styles.navbarTitle}>
                    {t(
                      `lessons.contentBlocks.evaluationBlock.evaluationResults`
                    )}
                  </span>
                </div>
                <div className="col col-lg-2 pr-5">
                  <span className={`${styles.onlyMobile}`}>
                    <button className="btn btn-primary" onClick={onClose}>
                      {t('lessons.contentBlocks.evaluationBlock.buttons.back')}
                    </button>
                  </span>
                </div>
              </div>

              <div className={`${styles.onlyDesktop} ${styles.sidebarContent}`}>
                <h2 className={styles.sidebarTitle}> {name}</h2>
                <span>
                  {t(`lessons.contentBlocks.evaluationBlock.evaluationResults`)}
                </span>
              </div>
            </div>
          </Col>
        )}
        <Col
          xs={12}
          sm={state === EvaluationStatesEnum.inProcess ? 12 : 10}
          className={`${
            state === EvaluationStatesEnum.inProcess ? 'p-0' : ''
          } d-flex justify-content-center ${styles.contentContainer}`}
        >
          <div className={`${styles.content} w-100`}>
            {evaluationData && state === EvaluationStatesEnum.onCheck && (
              <EvaluationAlert
                correct={`${evaluationData.correct}/${data.questions?.length}`}
                approved={evaluationData.approved}
                points={evaluationData.points}
                attempts={
                  (data.config?.maxRetries || 0) - evaluationData.attemps
                }
              />
            )}
            <div className="mb-3">
              <h2 className={styles.title}> {name}</h2>
            </div>
            <div className={`mb-5`}>
              {data.questions?.map(
                (question: IOldEvaluationQuestion, index: number) => (
                  <div
                    className="mb-4"
                    key={`question${index}-${question.questionableId}`}
                  >
                    <EvaluationQuestion
                      state={state}
                      data={question}
                      number={index + 1}
                      canEdit={
                        state === EvaluationStatesEnum.inProcess && canEdit
                      }
                      value={answers[index]}
                      onChange={(data: IUserAnswer) =>
                        handleSetAnswers(index, data)
                      }
                    />
                  </div>
                )
              )}
            </div>
            {state === EvaluationStatesEnum.inProcess && (
              <div className={styles.controls}>
                <div className={styles.rightAlign}>
                  <Button
                    className="m-0"
                    disabled={!canFinish}
                    size={ButtonSizes.Small}
                    variant={ButtonVariant.Success}
                    onClick={() => canFinish && handleSend()}
                  >
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    {t(`lessons.contentBlocks.evaluationBlock.buttons.finish`)}
                  </Button>
                </div>
              </div>
            )}
            {showResultsModal && evaluationData && (
              <EvaluationModal
                show={showResultsModal}
                onClose={handleOnClose}
                data={evaluationData}
                answers={answers}
                canEdit={canEdit}
                setOnEvaluation={() => setOnEvaluation(false)}
              />
            )}
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default Evaluation
